import React, { useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Alert, AlertColor, Box, Collapse, Stack, Typography } from '@mui/material';

import { handleExpandRowKeyDown } from './campaignScheduleTabLayout';

interface Props {
  title: string;
  issues: Array<string>;
  defaultExpanded: boolean;
  // "warning" for genuine issues that need attention, "info" for expected rule-based restrictions —
  // rules aren't problems, so they shouldn't read with the same urgency as a real issue.
  severity: AlertColor;
  // Singular noun for the count badge (e.g. "issue", "rule") — rules aren't "issues", so the badge
  // shouldn't call them that either.
  itemLabel: string;
}

export default function ScheduleDoctorIssueSection({ title, issues, defaultExpanded, severity, itemLabel }: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const hasIssues = issues.length > 0;
  const toggle = () => setExpanded((current) => !current);

  return (
    <Box>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        role={hasIssues ? 'button' : undefined}
        spacing={2}
        sx={{ bgcolor: 'background.paper', borderRadius: 1, cursor: hasIssues ? 'pointer' : 'default', px: 1, py: 1 }}
        tabIndex={hasIssues ? 0 : undefined}
        onClick={() => hasIssues && toggle()}
        onKeyDown={hasIssues ? (event) => handleExpandRowKeyDown(event, toggle) : undefined}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          {expanded ? (
            <KeyboardArrowDownIcon color={hasIssues ? undefined : 'disabled'} fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon color={hasIssues ? undefined : 'disabled'} fontSize="small" />
          )}
          <Typography fontWeight={600} variant="body1">
            {title}
          </Typography>
        </Stack>
        <Alert severity={hasIssues ? severity : 'success'} sx={{ py: 0 }}>
          {hasIssues ? `${issues.length} ${itemLabel}${issues.length === 1 ? '' : 's'}` : 'None detected'}
        </Alert>
      </Stack>
      {hasIssues && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box sx={{ bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
            <Stack spacing={1.5}>
              {issues.map((issue) => (
                <Alert key={issue} severity={severity}>
                  {issue}
                </Alert>
              ))}
            </Stack>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
